import BaseService from "react-lib/apis/services/BaseService";

import { getPurRecord, normalizePurRecord, PurOrderLine, PurReceiptLine, PurRecord } from "./purApi";
import { canUsePurDraftCreatePilot } from "./purDraftCreate";
import { getPurReceiptProduct } from "./purLookupsApi";
import { buildPurReceiptPayloadData, normalizePurPayloadDecimal, PurPayloadValidationError, receiptPayloadBaseCostCents, receiptPayloadOrderIssue } from "./purPayloadValidation";
import { getPur, PUR_REQUEST_TIMEOUT_MS, PurClientConfig, purRequestOptions, readDecimal, readId, readObject, readPage, readText } from "./purRead";

export interface PurReceiptItemInput {
  poItemId: number; productId: number; unitId: number;
  hasLots: boolean;
  delivered: string; accepted: string; rejected: string; reason: string;
  factor: string; unitCost: string; batch: string; manufactured: string; expires: string;
}
export interface PurReceiptMetadata { deliveryNumber: string; totalIncludingVat: string; notes: string }
export interface PurReceiptInput {
  po: PurRecord; storageId: number; rejectedStorageId: number | null; date: string;
  metadata: PurReceiptMetadata; items: PurReceiptItemInput[];
}
// The app binds this to the current authenticated API context. It deliberately
// has no user-controlled "ready" flag: the receipt and core stock readbacks
// are the evidence required before reporting a successful stock mutation.
export interface PurReceiptWriteAccess extends PurClientConfig {
  metadataContract?: {
    serialize: (metadata: PurReceiptMetadata) => Record<string, unknown>;
    matches: (receipt: Record<string, unknown>, metadata: PurReceiptMetadata, client: PurClientConfig) => boolean | Promise<boolean>;
  };
  preflightStockRead?: (client: PurClientConfig, input: PurReceiptInput, actorId: number) => Promise<void>;
  readStockSnapshot?: (client: PurClientConfig, input: PurReceiptInput, actorId: number) => Promise<string>;
  verifyStock: (client: PurClientConfig, receipt: PurRecord, input: PurReceiptInput, actorId: number) => Promise<boolean>;
}
export interface PurReceiptPermission { name: string; granted: boolean }
export interface PurReceiptRecoveryBaseline { receiptIds: number[]; stockSignature: string | null }
export interface PurReceiptAttempt { input: PurReceiptInput; actorId: number; permissions: PurReceiptPermission[]; backendReady: boolean; recovery?: PurReceiptRecoveryBaseline }
export interface PurReceiptResult { receipt: PurRecord; order: PurRecord }
export interface PurReceiptMismatch { messageId: string; values: Record<string, string | number> }
export interface PurReceiptFailure { httpStatus?: number; rollbackReported?: boolean; recovery?: PurReceiptRecoveryBaseline }
export type PurReceiptRecovery = { kind: "saved"; result: PurReceiptResult }
  | { kind: "rolledBack" }
  | { kind: "notFound"; order: PurRecord }
  | { kind: "candidates"; receipts: PurRecord[] };

export class PurReceiptError extends Error {
  constructor(public outcome: "blocked" | "unknown", public messageId: string, public receiptId: number | null = null, public mismatches: PurReceiptMismatch[] = [], public failure: PurReceiptFailure = {}) {
    super(messageId); Object.setPrototypeOf(this, PurReceiptError.prototype);
  }
}
const block = (messageId = "MEERP_RECEIPT_DATA_ERROR"): never => { throw new PurReceiptError("blocked", messageId); };
const visibleValue = (value: unknown): string => value === null || value === undefined || value === "" ? "—" : String(value);
const mismatch = (messageId: string, values: Record<string, unknown>): PurReceiptMismatch => ({ messageId,
  values: Object.fromEntries(Object.entries(values).map(([key, value]) => [key, visibleValue(value)])),
});
const dataError = (error: unknown): never => {
  if (error instanceof PurPayloadValidationError) return block(error.messageId);
  throw error;
};
const decimal = (text: string | null, places = 2, positive = false): string => {
  try { return normalizePurPayloadDecimal(text, places, positive, 12, "MEERP_RECEIPT_DATA_ERROR"); }
  catch (error) { return dataError(error); }
};
const units = (text: string | null, places = 2): number => Number(decimal(text, places).replace(".", ""));
export const receiptBaseCostCents = (cost: string, factor: string): number => {
  try { return receiptPayloadBaseCostCents(cost, factor); }
  catch (error) { return dataError(error); }
};
export const receiptOrderIssue = receiptPayloadOrderIssue;

export const preparePurReceiptOrder = async (client: PurClientConfig, id: number): Promise<PurRecord> => {
  if (!client.apiToken?.trim() || readId(client.divisionId) === null || readId(id) === null) return block("MEERP_RECEIPT_CONTEXT_ERROR");
  const po = await getPurRecord(client, "purchaseOrders", id);
  const issue = po.id === id ? receiptOrderIssue(po) : "MEERP_RECEIPT_SOURCE_ERROR";
  if (issue) return block(issue);
  const project = readObject(await getPur(client, `projects/${po.projectId}/`));
  if (readId(project.id) !== po.projectId || readId(project.division) !== readId(client.divisionId)) return block("MEERP_RECEIPT_SCOPE_ERROR");
  const products = new Map<number, Promise<boolean>>();
  await Promise.all(po.items.map(async (line) => {
    if (line.kind !== "purchaseOrders") return;
    if (!products.has(line.productId!)) products.set(line.productId!, getPurReceiptProduct(client, line.productId!, line.productName).then((product) => product.hasLots));
    try { line.hasLots = await products.get(line.productId!)!; }
    catch { return block("MEERP_RECEIPT_PRODUCT_ERROR"); }
  }));
  return po;
};

export const canWritePurReceipt = (client: PurClientConfig, access?: PurReceiptWriteAccess): boolean =>
  canUsePurDraftCreatePilot(client) && Boolean(typeof access?.verifyStock === "function" && access.apiToken === client.apiToken
    && readId(access.divisionId) === readId(client.divisionId)
    && purRequestOptions(access, "").url === purRequestOptions(client, "").url);

export const buildPurReceiptPayload = (input: PurReceiptInput): Record<string, unknown> => {
  try { return buildPurReceiptPayloadData(input); }
  catch (error) { return dataError(error); }
};

const CORE_PAGE_SIZE = 100;
const CORE_MAX_PAGES = 5;
type CoreRow = Record<string, unknown>;
const corePage = async (client: PurClientConfig, path: string): Promise<CoreRow[]> => {
  const core = { ...client, pathPrefix: "/apis/core" };
  const first = await getPur(core, `${path}${path.includes("?") ? "&" : "?"}limit=${CORE_PAGE_SIZE}&offset=0`);
  const page = readPage(first, readObject);
  if (page.count > CORE_PAGE_SIZE * CORE_MAX_PAGES) throw new Error("Core read exceeds bounded receipt verification");
  const rows = [...page.records];
  for (let offset = CORE_PAGE_SIZE; offset < page.count; offset += CORE_PAGE_SIZE) {
    const next = readPage(await getPur(core, `${path}&limit=${CORE_PAGE_SIZE}&offset=${offset}`), readObject);
    if (next.count !== page.count) throw new Error("Core page changed during receipt verification");
    rows.push(...next.records);
  }
  const ids = rows.map((row) => readId(row.id));
  if (rows.length !== page.count || ids.some((id) => id === null) || new Set(ids).size !== ids.length) {
    throw new Error("Core receipt verification page is incomplete");
  }
  return rows;
};
const coreQuery = (path: string, values: Record<string, string | number | null | undefined>): string => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => { if (value !== null && value !== undefined && value !== "") params.set(key, String(value)); });
  return `${path}?${params.toString()}`;
};
const isAddPerform = (value: unknown): boolean => ["12", "ADD_PERFORM"].includes(readText(value));
const integerDecimal = (value: unknown): number | null => {
  const text = readDecimal(value);
  if (text === null || !/^-?\d+$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) ? parsed : null;
};
const decimalCents = (value: unknown): number | null => {
  const text = readDecimal(value);
  if (text === null) return null;
  try { return units(text); } catch { return null; }
};
const stockLotId = (stock: CoreRow): number | null => readId(stock.lot);
const stockMatches = (stock: CoreRow, storageId: number, productId: number, lotId: number | null): boolean =>
  readId(stock.id) !== null && readId(stock.storage) === storageId && readId(stock.product) === productId && stockLotId(stock) === lotId;
const baseQuantity = (quantity: string, factor: string): number => units(quantity) * units(factor, 4) / 1000000;

interface StockExpectation { storageId: number; productId: number; lotId: number | null; batch: string; quantity: number; cost: number; reference: string }
const stockExpectations = (receipt: PurRecord, input: PurReceiptInput): StockExpectation[] => {
  const sources = [...input.items];
  const expectations: StockExpectation[] = [];
  receipt.items.filter((item): item is PurReceiptLine => item.kind === "receipts").forEach((row) => {
    const index = sources.findIndex((item) => item.poItemId === row.poItemId && item.productId === row.productId
      && item.unitId === row.unitId && item.batch.trim() === row.batchNumber && decimal(item.delivered) === decimal(row.deliveredQuantity)
      && decimal(item.accepted) === decimal(row.acceptedQuantity) && decimal(item.rejected) === decimal(row.rejectedQuantity)
      && decimal(item.unitCost) === decimal(row.unitCost) && decimal(item.factor, 4) === decimal(row.conversionFactor, 4));
    if (index < 0) throw new Error("Receipt item cannot be tied to the confirmed lot");
    const source = sources.splice(index, 1)[0];
    const expectedCost = receiptBaseCostCents(source.unitCost, source.factor);
    const accepted = baseQuantity(source.accepted, source.factor), rejected = baseQuantity(source.rejected, source.factor);
    if (!Number.isSafeInteger(accepted) || !Number.isSafeInteger(rejected)) throw new Error("Receipt base quantity is not exact");
    if (accepted > 0) expectations.push({ storageId: input.storageId, productId: source.productId, lotId: row.productLotId,
      batch: source.batch.trim(), quantity: accepted, cost: expectedCost, reference: `GRN: ${receipt.number}` });
    if (rejected > 0 && input.rejectedStorageId !== null) expectations.push({ storageId: input.rejectedStorageId, productId: source.productId, lotId: row.productLotId,
      batch: source.batch.trim(), quantity: rejected, cost: expectedCost, reference: `GRN Quarantine: ${receipt.number} (${source.reason.trim()})` });
  });
  if (sources.length) throw new Error("Receipt item readback is incomplete");
  return expectations;
};
const coreStockReadPaths = (input: PurReceiptInput, actorId: number): string[] => {
  const paths = new Set<string>();
  input.items.forEach((item) => {
    [input.storageId, item.rejected === "0" ? null : input.rejectedStorageId].forEach((storageId) => {
      if (storageId === null) return;
      paths.add(coreQuery("stock/", { products: item.productId, storages: storageId }));
      paths.add(coreQuery("stock-logs/", { stock__storage: storageId, stock__product: item.productId, stock__lot__mfc_no: item.hasLots ? item.batch.trim() : null, edit_user: actorId, type: 12 }));
    });
  });
  return [...paths];
};

export const preflightPurReceiptStockRead = async (client: PurClientConfig, input: PurReceiptInput, actorId: number): Promise<void> => {
  await Promise.all(coreStockReadPaths(input, actorId).map((path) => corePage(client, path)));
};

// Canonicalize GET results so object-key and row ordering cannot manufacture a
// stock change. This baseline stays in memory; it is never sent in a receipt.
const canonicalJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value !== null && typeof value === "object") return `{${Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
    .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`).join(",")}}`;
  return JSON.stringify(value) ?? "null";
};
export const readPurReceiptStockSnapshot = async (client: PurClientConfig, input: PurReceiptInput, actorId: number): Promise<string> => {
  const paths = coreStockReadPaths(input, actorId).sort();
  const pages = await Promise.all(paths.map(async (path) => [path, (await corePage(client, path)).sort((a, b) => Number(a.id) - Number(b.id))]));
  return canonicalJson(pages);
};

const receiptHistory = async (client: PurClientConfig, poId: number): Promise<PurRecord[]> => {
  const limit = 100, records: PurRecord[] = [];
  let count = 0;
  for (let offset = 0; offset === 0 || offset < count; offset += limit) {
    const page = readPage(await getPur(client, `receipts/?po=${poId}&limit=${limit}&offset=${offset}`), (raw) => normalizePurRecord("receipts", raw));
    if (page.count > 500 || (offset > 0 && page.count !== count) || page.records.length !== Math.min(limit, page.count - offset)
      || page.records.some((record) => record.poId !== poId)) throw new Error("Receipt history is incomplete or not filtered by PO");
    count = page.count; records.push(...page.records);
  }
  if (new Set(records.map((record) => record.id)).size !== count) throw new Error("Receipt history contains duplicate IDs");
  return records;
};

export const verifyPurReceiptStock = async (client: PurClientConfig, receipt: PurRecord, input: PurReceiptInput, actorId: number): Promise<boolean> => {
  const expected = stockExpectations(receipt, input);
  if (!expected.length) return false;
  const stocks = new Map<string, CoreRow[]>(), logs = new Map<string, CoreRow[]>();
  for (const row of expected) {
    const key = `${row.storageId}:${row.productId}`;
    if (!stocks.has(key)) stocks.set(key, await corePage(client, coreQuery("stock/", { products: row.productId, storages: row.storageId })));
    const logKey = `${key}:${row.lotId ?? "none"}:${row.lotId === null ? "" : row.batch}`;
    if (!logs.has(logKey)) logs.set(logKey, await corePage(client, coreQuery("stock-logs/", {
      stock__storage: row.storageId, stock__product: row.productId, stock__lot__mfc_no: row.lotId === null ? null : row.batch,
    })));
  }
  const unmatched = new Map<string, CoreRow[]>();
  logs.forEach((rows, key) => unmatched.set(key, [...rows]));
  const matches = expected.every((row) => {
    const stockKey = `${row.storageId}:${row.productId}`;
    const stock = (stocks.get(stockKey) || []).find((candidate) => stockMatches(candidate, row.storageId, row.productId, row.lotId));
    const logKey = `${stockKey}:${row.lotId ?? "none"}:${row.lotId === null ? "" : row.batch}`;
    const candidates = unmatched.get(logKey) || [];
    const index = candidates.findIndex((log) => readId(log.related_issue_id) === receipt.id && readText(log.related_issue_type_str) === "purchasereceipt"
      && stock !== undefined && readId(log.stock) === readId(stock.id) && isAddPerform(log.type) && integerDecimal(log.quantity) === row.quantity
      && decimalCents(log.cost) === row.cost && readId(log.edit_user) === actorId && readText(log.reference_text) === row.reference);
    if (index < 0) return false;
    candidates.splice(index, 1); return true;
  });
  if (!matches) return false;
  // A second StockLog linked to this GR in the same stock/lot group could mean
  // a duplicate mutation. It is not harmless evidence for this submission.
  if ([...unmatched.values()].some((rows) => rows.some((log) => readId(log.related_issue_id) === receipt.id
    && readText(log.related_issue_type_str) === "purchasereceipt"))) return false;
  const expectedStocks = new Map<number, CoreRow>();
  expected.forEach((row) => {
    const stock = (stocks.get(`${row.storageId}:${row.productId}`) || []).find((candidate) => stockMatches(candidate, row.storageId, row.productId, row.lotId));
    if (stock) expectedStocks.set(readId(stock.id)!, stock);
  });
  const allRelevantLogs = [...logs.values()].flatMap((rows) => rows);
  return expectedStocks.size > 0 && [...expectedStocks.values()].every((stock) => {
    const quantity = integerDecimal(stock.quantity);
    const latest = allRelevantLogs.filter((log) => readId(log.stock) === readId(stock.id)).reduce<CoreRow | null>((current, log) =>
      !current || (readId(log.id) || 0) > (readId(current.id) || 0) ? log : current, null);
    return quantity !== null && latest !== null && integerDecimal(latest.balance) === quantity;
  });
};

export const createPurReceiptRuntimeAccess = (client: PurClientConfig): PurReceiptWriteAccess => ({
  ...client,
  preflightStockRead: preflightPurReceiptStockRead,
  readStockSnapshot: readPurReceiptStockSnapshot,
  verifyStock: verifyPurReceiptStock,
});

const signature = (po: PurRecord, includeLotPolicy = true): string => JSON.stringify({ id: po.id, status: po.status, edited: po.editedAt,
  project: po.projectId, vendor: po.vendorId, storage: po.storageId, billed: po.percentBilled, received: po.percentReceived,
  items: po.items.map((line) => line.kind === "purchaseOrders" ? [line.id, line.productId, line.unitId, includeLotPolicy ? line.hasLots : null, decimal(line.conversionFactor, 4),
    decimal(line.orderedQuantity), decimal(line.receivedQuantity), decimal(line.unitCost)] : []).sort((a, b) => Number(a[0]) - Number(b[0])) });
const permissionQueries = (input: PurReceiptInput): Array<{ key: string; name: string }> => {
  const totals = new Map<number, number>();
  input.items.forEach((line) => totals.set(line.poItemId, (totals.get(line.poItemId) || 0) + units(line.accepted)));
  const full = input.po.items.every((line) => line.kind === "purchaseOrders"
    && units(line.receivedQuantity) + (totals.get(line.id!) || 0) === units(line.orderedQuantity));
  if (full && input.po.percentBilled === null) return block("MEERP_RECEIPT_BILLING_UNKNOWN");
  const action = full ? (units(input.po.percentBilled) >= 10000 ? "COMPLETE" : "RECEIVE_FULL") : "RECEIVE_PARTIAL";
  return [ { key: "perm_users_CREATE_PUR:receipt-receive-goods", name: "users.CREATE_PUR:receipt-receive-goods" },
    ...["CREATE", "VERIFY"].map((name) => ({ key: `action_PUR_PurchaseReceipt_${name}`, name: `users.ACTION_PUR.PurchaseReceipt:${name}` })),
    { key: `action_PUR_PurchaseOrder_${action}`, name: `users.ACTION_PUR.PurchaseOrder:${action}` },
    { key: "perm_users_RETRIEVE_core:list_product", name: "users.RETRIEVE_core:list_product" },
    { key: "perm_users_RETRIEVE_core:list_stock", name: "users.RETRIEVE_core:list_stock" },
    { key: "perm_users_RETRIEVE_core:list_stock_logs", name: "users.RETRIEVE_core:list_stock_logs" } ];
};

export const preparePurReceipt = async (client: PurClientConfig, input: PurReceiptInput, access?: PurReceiptWriteAccess): Promise<PurReceiptAttempt> => {
  if (!canUsePurDraftCreatePilot(client)) return block("MEERP_RECEIPT_CONTEXT_ERROR");
  buildPurReceiptPayload(input);
  const po = await preparePurReceiptOrder(client, input.po.id);
  if (signature(po) !== signature(input.po)) return block("MEERP_RECEIPT_CHANGED");
  if (input.storageId !== readId(po.storageId)) return block("MEERP_RECEIPT_SCOPE_ERROR");
  const project = readObject(await getPur(client, `projects/${po.projectId}/`));
  const vendor = readObject(await getPur(client, `vendors/${po.vendorId}/`));
  if (readId(project.id) !== po.projectId || readId(project.division) !== readId(client.divisionId) || project.active !== true
    || readId(vendor.id) !== po.vendorId) return block("MEERP_RECEIPT_SCOPE_ERROR");
  const profile = readObject(await getPur({ ...client, pathPrefix: "/users/apis" }, "users/profile/"));
  const actorId = readId(profile.user);
  if (actorId === null) return block("MEERP_RECEIPT_ACTOR_ERROR");
  const queries = permissionQueries(input), request: Record<string, boolean> = {};
  queries.forEach(({ key }) => { request[key] = false; });
  const { url, headers } = purRequestOptions({ ...client, pathPrefix: "/users/apis" }, "user-permission/");
  const response = await new BaseService().client.post(url, request, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
  const data = readObject(response.data);
  const permissions = queries.map(({ key, name }) => ({ name, granted: data[key] === true }));
  let recovery: PurReceiptRecoveryBaseline | undefined;
  if (canWritePurReceipt(client, access) && permissions.every((permission) => permission.granted)) {
    let stockSignature: string | null = null;
    try {
      if (access!.readStockSnapshot) stockSignature = await access!.readStockSnapshot(client, input, actorId);
      else if (access!.preflightStockRead) await access!.preflightStockRead(client, input, actorId);
    }
    catch { return block("MEERP_RECEIPT_STOCK_READ_ERROR"); }
    try { recovery = { receiptIds: (await receiptHistory(client, input.po.id)).map((record) => record.id), stockSignature }; }
    catch { return block("MEERP_RECEIPT_HISTORY_READ_ERROR"); }
  }
  return { input: JSON.parse(JSON.stringify(input)) as PurReceiptInput, actorId,
    permissions, backendReady: canWritePurReceipt(client, access), recovery };
};

export const reconcilePurReceipt = async (client: PurClientConfig, attempt: PurReceiptAttempt, id: number, access?: PurReceiptWriteAccess): Promise<PurReceiptResult> => {
  if (!canWritePurReceipt(client, access) || readId(id) === null) return block("MEERP_RECEIPT_BACKEND_PENDING");
  const raw = readObject(await getPur(client, `receipts/${id}/`));
  const receipt = normalizePurRecord("receipts", raw);
  const expected = buildPurReceiptPayload(attempt.input).items as Array<Record<string, unknown>>;
  const rows = receipt.items.filter((line): line is PurReceiptLine => line.kind === "receipts");
  const unmatched = [...rows];
  const receiptMismatches: PurReceiptMismatch[] = [];
  const receiptField = (field: string, expectedValue: unknown, actual: unknown) => {
    if (visibleValue(expectedValue) !== visibleValue(actual)) receiptMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_GR_FIELD", { field, expected: expectedValue, actual }));
  };
  receiptField("id", id, receipt.id); receiptField("status", "VERIFIED", receipt.status);
  receiptField("po", attempt.input.po.id, receipt.poId); receiptField("project", attempt.input.po.projectId, receipt.projectId);
  receiptField("storage", attempt.input.storageId, readId(receipt.storageId)); receiptField("rejected_storage", attempt.input.rejectedStorageId, receipt.rejectedStorageId);
  receiptField("receipt_date", attempt.input.date, receipt.date); receiptField("received_by", attempt.actorId, readId(raw.received_by));
  if (!readText(raw.grn_number)) receiptMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_GR_FIELD", { field: "grn_number", expected: "non-empty", actual: raw.grn_number }));
  receiptField("items.length", expected.length, rows.length);
  if (new Set(rows.map((row) => row.id)).size !== rows.length || rows.some((row) => readId(row.id) === null || row.receiptId !== id)) {
    receiptMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_GR_FIELD", { field: "receipt_item.id/receipt", expected: `unique IDs linked to ${id}`, actual: "missing, duplicate, or linked to another receipt" }));
  }
  expected.forEach((item, position) => {
    const source = attempt.input.items[position];
    const sameSource = (row: PurReceiptLine) => row.poItemId === item.po_item && row.productId === source.productId;
    // Read-back item ordering is not part of the API contract, including split Lots.
    let index = unmatched.findIndex((row) => sameSource(row) && row.batchNumber === item.batch_no
      && decimal(row.deliveredQuantity) === item.received_qty && decimal(row.acceptedQuantity) === item.accepted_qty
      && decimal(row.rejectedQuantity) === item.rejected_qty && row.unitId === item.unit
      && decimal(row.conversionFactor, 4) === item.conversion_factor && decimal(row.unitCost) === item.unit_cost
      && row.rejectionReason === item.rejection_reason && visibleValue(row.manufactureDate) === visibleValue(item.mfg_date)
      && visibleValue(row.expiryDate) === visibleValue(item.exp_date));
    if (index < 0) index = unmatched.findIndex((row) => sameSource(row) && row.batchNumber === item.batch_no);
    if (index < 0) index = unmatched.findIndex(sameSource);
    if (index < 0) {
      receiptMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_GR_LINE_FIELD", { item: item.po_item, field: "PO item/product", expected: `${item.po_item}/${source.productId}`, actual: "not found" }));
      return;
    }
    const row = unmatched.splice(index, 1)[0];
    ([
      ["batch_no", item.batch_no, row.batchNumber], ["unit", item.unit, row.unitId],
      ["received_qty", item.received_qty, decimal(row.deliveredQuantity)], ["accepted_qty", item.accepted_qty, decimal(row.acceptedQuantity)],
      ["rejected_qty", item.rejected_qty, decimal(row.rejectedQuantity)], ["conversion_factor", item.conversion_factor, decimal(row.conversionFactor, 4)],
      ["unit_cost", item.unit_cost, decimal(row.unitCost)], ["rejection_reason", item.rejection_reason, row.rejectionReason],
      ["product_lot", source.hasLots ? "present" : null, source.hasLots ? (row.productLotId === null ? null : "present") : row.productLotId],
      ["mfg_date", item.mfg_date, row.manufactureDate], ["exp_date", item.exp_date, row.expiryDate],
    ] as Array<[string, unknown, unknown]>).forEach(([field, expectedValue, actual]) => {
      if (visibleValue(expectedValue) !== visibleValue(actual)) receiptMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_GR_LINE_FIELD", { item: item.po_item, field, expected: expectedValue, actual }));
    });
  });
  if (unmatched.length) receiptMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_GR_FIELD", { field: "unexpected receipt items", expected: 0, actual: unmatched.length }));
  // Metadata persistence is not present in the known receipt serializer. A
  // future vendor-document contract may opt in here; its absence never turns
  // a verified stock receipt into a fabricated metadata success claim.
  if (access!.metadataContract && !await access!.metadataContract.matches(raw, attempt.input.metadata, client)) receiptMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_GR_FIELD", { field: "linked document metadata", expected: "matches submitted review data", actual: "does not match or is unavailable" }));
  if (receiptMismatches.length) throw new PurReceiptError("unknown", "MEERP_RECEIPT_READBACK_ERROR", id, receiptMismatches);
  const order = await getPurRecord(client, "purchaseOrders", attempt.input.po.id);
  const totals = new Map<number, number>();
  attempt.input.items.forEach((line) => totals.set(line.poItemId, (totals.get(line.poItemId) || 0) + units(line.accepted)));
  const orderMismatches: PurReceiptMismatch[] = [];
  const orderField = (field: string, expectedValue: unknown, actual: unknown) => {
    if (visibleValue(expectedValue) !== visibleValue(actual)) orderMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_PO_FIELD", { field, expected: expectedValue, actual }));
  };
  orderField("id", attempt.input.po.id, order.id); orderField("project", attempt.input.po.projectId, order.projectId); orderField("vendor", attempt.input.po.vendorId, order.vendorId);
  orderField("items.length", attempt.input.po.items.length, order.items.length);
  attempt.input.po.items.forEach((before) => {
    if (before.kind !== "purchaseOrders") return;
    const after = order.items.find((line) => line.id === before.id);
    if (!after || after.kind !== "purchaseOrders") {
      orderMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_PO_LINE_FIELD", { item: before.id, field: "PO item", expected: "present", actual: "not found" })); return;
    }
    ([
      ["product", before.productId, after.productId], ["unit", before.unitId, after.unitId],
      ["conversion_factor", decimal(before.conversionFactor, 4), decimal(after.conversionFactor, 4)], ["ordered_qty", decimal(before.orderedQuantity), decimal(after.orderedQuantity)],
      ["received_qty", units(before.receivedQuantity) + (totals.get(before.id!) || 0), units(after.receivedQuantity)],
    ] as Array<[string, unknown, unknown]>).forEach(([field, expectedValue, actual]) => {
      if (visibleValue(expectedValue) !== visibleValue(actual)) orderMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_PO_LINE_FIELD", { item: before.id, field, expected: expectedValue, actual }));
    });
  });
  // Billing may happen before receipt. The server owns the resulting PO
  // status; quantity and StockLog readbacks above remain the receipt proof.
  if (!["APPROVED", "PARTIALLY_RECEIVED", "TO_BILL", "TO_RECEIVE", "COMPLETED"].includes(order.status)) {
    orderMismatches.push(mismatch("MEERP_RECEIPT_MISMATCH_PO_FIELD", { field: "status", expected: "a receivable PO status", actual: order.status }));
  }
  if (orderMismatches.length) throw new PurReceiptError("unknown", "MEERP_RECEIPT_READBACK_ERROR", id, orderMismatches);
  if (!await access!.verifyStock(client, receipt, attempt.input, attempt.actorId)) throw new PurReceiptError("unknown", "MEERP_RECEIPT_STOCK_PENDING", id);
  return { receipt, order };
};

// Finding a GR is not success by itself. The existing GR/PO/StockLog checks
// remain authoritative, and a missing GR alone never authorizes another POST.
export const findPurReceiptOutcome = async (client: PurClientConfig, attempt: PurReceiptAttempt, failure: PurReceiptFailure = {}, access?: PurReceiptWriteAccess): Promise<PurReceiptRecovery> => {
  if (!canWritePurReceipt(client, access)) return block("MEERP_RECEIPT_BACKEND_PENDING");
  const history = await receiptHistory(client, attempt.input.po.id);
  const baseline = failure.recovery || attempt.recovery;
  const candidates = history.filter((record) => !baseline?.receiptIds.includes(record.id));
  if (candidates.length === 1 && baseline) return { kind: "saved", result: await reconcilePurReceipt(client, attempt, candidates[0].id, access) };
  if (candidates.length > 0) return { kind: "candidates", receipts: candidates };
  const order = await getPurRecord(client, "purchaseOrders", attempt.input.po.id);
  const project = readObject(await getPur(client, `projects/${order.projectId}/`));
  if (order.id !== attempt.input.po.id || readId(project.id) !== order.projectId || readId(project.division) !== readId(client.divisionId)) return block("MEERP_RECEIPT_SCOPE_ERROR");
  const sameHistory = baseline && canonicalJson([...baseline.receiptIds].sort((a, b) => a - b)) === canonicalJson(history.map((record) => record.id).sort((a, b) => a - b));
  if (failure.rollbackReported === true && sameHistory && baseline?.stockSignature && access!.readStockSnapshot
    && signature(order, false) === signature(attempt.input.po, false)
    && await access!.readStockSnapshot(client, attempt.input, attempt.actorId) === baseline.stockSignature) return { kind: "rolledBack" };
  return { kind: "notFound", order };
};

export const receivePurGoods = async (client: PurClientConfig, attempt: PurReceiptAttempt, access?: PurReceiptWriteAccess, isCurrent: () => boolean = () => true): Promise<PurReceiptResult> => {
  if (!canWritePurReceipt(client, access)) return block("MEERP_RECEIPT_BACKEND_PENDING");
  let fresh: PurReceiptAttempt;
  try { fresh = await preparePurReceipt(client, attempt.input, access); }
  catch (error: unknown) { throw error instanceof PurReceiptError ? error : new PurReceiptError("blocked", "MEERP_RECEIPT_CHECK_ERROR"); }
  if (!isCurrent() || fresh.actorId !== attempt.actorId) return block("MEERP_RECEIPT_CONTEXT_ERROR");
  if (fresh.permissions.some((permission) => !permission.granted)) return block("MEERP_RECEIPT_PERMISSION_MISSING");
  const base = buildPurReceiptPayload(attempt.input);
  const metadata = access!.metadataContract ? readObject(access!.metadataContract.serialize(attempt.input.metadata)) : {};
  if (Object.keys(metadata).some((key) => key in base || ["action", "status", "id", "user", "grn_number"].includes(key))) return block("MEERP_RECEIPT_BACKEND_PENDING");
  const { url, headers } = purRequestOptions(client, "receipts/receive-goods/");
  let response: { status: number; data: unknown };
  try {
    response = await new BaseService().client.post(url, { ...base, ...metadata }, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
  } catch (error: unknown) {
    // Only read structured diagnostic fields, never HTML, headers or raw errors.
    let httpStatus: number | undefined, rollbackReported = false;
    try {
      const rejected = readObject(readObject(error).response);
      if (typeof rejected.status === "number" && Number.isInteger(rejected.status)) httpStatus = rejected.status;
      rollbackReported = httpStatus === 500 && readObject(rejected.data).status === "ROLLED_BACK";
    } catch { /* A timeout/unstructured response is still unknown. */ }
    throw new PurReceiptError("unknown", "MEERP_RECEIPT_UNKNOWN", null, [], { httpStatus, rollbackReported, recovery: fresh.recovery });
  }
  let id: number | null = null;
  try {
    id = readId(readObject(response.data).id);
    if (![200, 201].includes(response.status) || id === null) throw new Error("Unexpected receipt response");
    return await reconcilePurReceipt(client, fresh, id, access);
  } catch (error: unknown) {
    // A GET/serialization failure after a successful POST is not a rollback.
    throw new PurReceiptError("unknown", error instanceof PurReceiptError ? error.messageId : "MEERP_RECEIPT_UNKNOWN", id,
      error instanceof PurReceiptError ? error.mismatches : [], { recovery: fresh.recovery });
  }
};
